import React, { useState } from "react";
import { Button, Card, Col, Row, Stack } from "react-bootstrap";
import { unsavedChanges } from "../../../App";
import Input from "../../ui/Input";
import SelectObject from "../../ui/SelectObject";
import { deleteField, patchField } from "../../../services/httpClient";
import IconButton from "../../ui/IconButton";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";

interface FieldState {
  field: any;
  getFields: any;
  setOrgToSave: any;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const FieldCard = ({ field, getFields, setOrgToSave, setErrMessage, setShowToast, setToastText, setToastBody }: FieldState) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [label, setLabel] = useState<string>(field?.label);
  const [type, setType] = useState<string>(field?.type);
  const [saveEnabled, setSaveEnabled] = useState(false);

  const fieldOptions = [
    { key: "checkbox", name: "Checkbox" },
    { key: "date", name: "Date" },
    { key: "text", name: "Text" },
    { key: "multi-line text", name: "Multi-Line Text" },
  ];

  const onClickEdit = () => {
    setIsEditing(true);
  };

  const onClickEditFinal = () => {
    if (!label) {
      setErrMessage("Cannot set null label");
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
      return;
    }

    const updatedField = {
      label: label,
      type: type,
    };

    setErrMessage("");

    patchField(updatedField, field?.id)
      .then((res: any) => {
        setSaveEnabled(false);
        unsavedChanges.value = false;
        setOrgToSave(false);
        setShowToast(true);
        setToastText("Field Updated");
        setToastBody(label);
        setIsEditing(false);
        getFields();
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelEdit = () => {
    unsavedChanges.value = false;
    setOrgToSave(false);
    setIsEditing(false);
    setErrMessage(undefined);
  };

  const onUpdateLabel = (e: any) => {
    setLabel(e.target.value);
    setSaveEnabled(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMessage(undefined);
  };

  const onUpdateType = (e: any) => {
    setType(e.target.value);
    setSaveEnabled(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMessage(undefined);
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  const onClickDeleteFinal = () => {
    deleteField(field?.id)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Field Deleted");
        setToastBody(label);
        setIsDeleting(false);
        getFields();
      })
      .catch((err: any) => {
        setIsDeleting(false);
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setErrMessage(undefined);
  };

  const renderEdit = () => {
    return (
      <Stack direction="horizontal" gap={3}>
        <Col>
          <Input label="Label" type="text" value={label} onUpdate={(e: any) => onUpdateLabel(e)} required={true} hasFocus={true} />
        </Col>
        <Col>
          <SelectObject label="Type" value={type} options={fieldOptions} onUpdate={(e: any) => onUpdateType(e)} required={true} />
        </Col>
        <Button className="app-secondary-color button" onClick={onClickEditFinal} disabled={!saveEnabled}>
          Save
        </Button>
        <div className="vr" />
        <Button className="app-primary-color button" onClick={onClickCancelEdit}>
          Cancel
        </Button>
      </Stack>
    );
  };

  const renderDelete = () => {
    return (
      <Row>
        <Col xs={6}>
          <Stack style={{ justifyContent: "center" }}>
            <Card.Subtitle>Are you sure you want to delete {label}?</Card.Subtitle>
          </Stack>
        </Col>
        <Col xs={6} className="d-flex justify-content-end">
          <Stack direction="horizontal" gap={3}>
            <Button className="app-danger-color button" onClick={onClickDeleteFinal}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={onClickCancelDelete}>
              Cancel
            </Button>
          </Stack>
        </Col>
      </Row>
    );
  };

  return (
    <>
      <Card className="bg-light mt-3">
        <Card.Body>
          {isEditing ? (
            <>{renderEdit()}</>
          ) : isDeleting ? (
            <>{renderDelete()}</>
          ) : (
            <Stack direction="horizontal" gap={3}>
              <Stack gap={2} className="align-self-center">
                <Card.Subtitle className="text-muted">
                  <small className="text-muted">{label}</small>
                </Card.Subtitle>
              </Stack>
              <small>{type}</small>
              <div className="vr" />
              <IconButton onClickButton={() => onClickEdit()} tooltipText={"Edit Field"} icon={faEdit} />
              <div className="vr" />
              <IconButton onClickButton={() => onClickDelete()} tooltipText={"Delete Field"} icon={faTrash} />
            </Stack>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default FieldCard;
